/**
 * Sonido sintetizado con Web Audio: efectos y música procedural, sin archivos de audio.
 *
 * iOS exige crear/reanudar el AudioContext dentro de un gesto del usuario: unlock() se llama en
 * cada toque o tecla y es idempotente. Con el iPhone en modo silencio, Safari no reproduce sonido web.
 */
const STORAGE_KEY = 'dopamine-runner.sound.v1';
const MUSIC_KEY = 'dopamine-runner.music.v1';
const MASTER = 0.8;
const LOOKAHEAD = 0.15; // s de música programada por adelantado
const midi = (m) => 440 * 2 ** ((m - 69) / 12);

// Progresión menor y oscura: La m · Fa · Re m · Mi (notas MIDI de cada acorde).
const CHORDS = [
  [57, 60, 64],
  [53, 57, 60],
  [50, 53, 57],
  [52, 56, 59],
];
const ARP = [0, 1, 2, 1, 2, 3, 2, 1]; // índices sobre el acorde (3 = raíz una octava arriba)

export class Sound {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this.musicOn = true; // la música se puede quitar dejando los efectos
    try {
      this.enabled = localStorage.getItem(STORAGE_KEY) !== 'off';
      this.musicOn = localStorage.getItem(MUSIC_KEY) !== 'off';
    } catch {}
    this.mode = null; // música: 'menu' | 'run' | null
    this.intensity = 0; // 0..1 según la velocidad
    this.danger = false;
    this.coinStreak = 0;
    this.lastCoin = 0;
  }

  /** Crea o reanuda el contexto de audio. Llamar desde un gesto del usuario. */
  unlock() {
    // Fuera de un gesto real el navegador lo bloquearía (y avisaría en la consola).
    if (navigator.userActivation && !navigator.userActivation.isActive) return;
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = this.enabled ? MASTER : 0;
      const comp = ctx.createDynamicsCompressor();
      this.master.connect(comp).connect(ctx.destination);
      this.sfx = ctx.createGain();
      this.sfx.connect(this.master);
      this.music = ctx.createGain();
      this.music.gain.value = 0.3;
      this.music.connect(this.master);
      // Ruido blanco reutilizable para golpes, viento y platillos.
      this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      // iOS: reproducir algo dentro del gesto desbloquea la salida de audio.
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, 22050);
      src.connect(ctx.destination);
      src.start(0);
      this.step = 0;
      this.nextNote = ctx.currentTime + 0.1;
      setInterval(() => this.#schedule(), 25);
    }
    if (this.ctx.state !== 'running' && !document.hidden) this.ctx.resume();
  }

  get ready() {
    return this.ctx && this.ctx.state === 'running';
  }

  setEnabled(on) {
    this.enabled = on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {}
    if (this.ctx) this.master.gain.setTargetAtTime(on ? MASTER : 0, this.ctx.currentTime, 0.05);
  }

  setMusicOn(on) {
    this.musicOn = on;
    try {
      localStorage.setItem(MUSIC_KEY, on ? 'on' : 'off');
    } catch {}
  }

  /** Al pasar a segundo plano se para todo; al volver se reanuda. */
  setHidden(hidden) {
    if (!this.ctx) return;
    if (hidden) this.ctx.suspend();
    else this.ctx.resume();
  }

  setMusic(mode) {
    if (mode === this.mode) return;
    this.mode = mode;
    if (this.ctx) {
      this.step = 0;
      this.nextNote = this.ctx.currentTime + 0.05;
    }
  }

  // ---------- Efectos ----------

  play(name, arg) {
    if (!this.ready || !this.enabled) return;
    const t = this.ctx.currentTime;
    switch (name) {
      case 'coin': {
        // Cada moneda seguida suena un poco más aguda.
        this.coinStreak = t - this.lastCoin < 0.6 ? Math.min(this.coinStreak + 1, 12) : 0;
        this.lastCoin = t;
        const f = 1320 * 2 ** (this.coinStreak / 24);
        this.#tone({ type: 'square', freq: f, dur: 0.05, vol: 0.06, t });
        this.#tone({ type: 'sine', freq: f * 1.5, dur: 0.12, vol: 0.12, t: t + 0.045 });
        break;
      }
      case 'gem':
        [84, 88, 91, 96].forEach((n, i) => this.#tone({ type: 'sine', freq: midi(n), dur: 0.12, vol: 0.12, t: t + i * 0.045 }));
        this.#tone({ type: 'triangle', freq: midi(103), dur: 0.3, vol: 0.05, t: t + 0.18 });
        break;
      case 'jump':
        this.#tone({ type: 'triangle', freq: 280, freqEnd: 720, dur: 0.16, vol: 0.18, t });
        this.#noise({ dur: 0.14, vol: 0.06, type: 'highpass', freq: 2500, t });
        break;
      case 'step':
        this.#noise({ dur: 0.05, vol: 0.05, type: 'lowpass', freq: 500 + Math.random() * 300, t });
        break;
      case 'land':
        this.#tone({ type: 'sine', freq: 130, freqEnd: 55, dur: 0.12, vol: 0.25, t });
        this.#noise({ dur: 0.08, vol: 0.08, type: 'lowpass', freq: 700, t });
        break;
      case 'slide':
        this.#noise({ dur: 0.38, vol: 0.14, type: 'bandpass', freq: 1800, freqEnd: 450, q: 1.2, t });
        break;
      case 'lane':
        this.#noise({ dur: 0.1, vol: 0.06, type: 'bandpass', freq: 1100, freqEnd: 2600, q: 1.5, t });
        break;
      case 'turn':
        this.#noise({ dur: 0.2, vol: 0.09, type: 'bandpass', freq: 600, freqEnd: 1600, q: 1, t });
        break;
      case 'stumble':
        this.#tone({ type: 'sine', freq: 95, freqEnd: 38, dur: 0.3, vol: 0.5, t });
        this.#noise({ dur: 0.18, vol: 0.25, type: 'lowpass', freq: 900, t });
        this.#growl(t + 0.12, 0.7);
        break;
      case 'smash':
        this.#noise({ dur: 0.4, vol: 0.35, type: 'lowpass', freq: 2200, freqEnd: 250, t });
        this.#tone({ type: 'sine', freq: 80, freqEnd: 40, dur: 0.25, vol: 0.35, t });
        break;
      case 'crash':
        this.#tone({ type: 'sine', freq: 110, freqEnd: 30, dur: 0.5, vol: 0.6, t });
        this.#noise({ dur: 0.5, vol: 0.4, type: 'lowpass', freq: 1600, freqEnd: 200, t });
        this.#tone({ type: 'sawtooth', freq: 300, freqEnd: 80, dur: 0.6, vol: 0.06, t: t + 0.05 });
        break;
      case 'fall':
        this.#tone({ type: 'sine', freq: 900, freqEnd: 110, dur: 1.1, vol: 0.18, t });
        this.#noise({ dur: 1.1, vol: 0.1, type: 'bandpass', freq: 500, freqEnd: 200, q: 0.7, t });
        break;
      case 'caught':
        this.#growl(t, 1.2);
        this.#tone({ type: 'sine', freq: 90, freqEnd: 30, dur: 0.5, vol: 0.5, t: t + 0.2 });
        break;
      case 'growl':
        this.#growl(t, 0.9);
        break;
      case 'powerUp': {
        const root = { magnet: 72, shield: 76, sprint: 79 }[arg] ?? 72;
        [0, 4, 7, 12].forEach((n, i) => this.#tone({ type: 'triangle', freq: midi(root + n), dur: 0.14, vol: 0.16, t: t + i * 0.06 }));
        this.#tone({ type: 'sine', freq: midi(root + 24), dur: 0.35, vol: 0.06, t: t + 0.24 });
        break;
      }
      case 'powerDown':
        this.#tone({ type: 'triangle', freq: midi(76), dur: 0.1, vol: 0.12, t });
        this.#tone({ type: 'triangle', freq: midi(69), dur: 0.18, vol: 0.12, t: t + 0.09 });
        break;
      case 'beep':
        this.#tone({ type: 'square', freq: arg ? 990 : 660, dur: arg ? 0.3 : 0.12, vol: 0.08, t });
        break;
      case 'click':
        this.#tone({ type: 'sine', freq: 880, dur: 0.05, vol: 0.12, t });
        break;
      case 'gameOver':
        [64, 60, 57, 52].forEach((n, i) => this.#tone({ type: 'triangle', freq: midi(n), dur: 0.3, vol: 0.14, t: t + 0.3 + i * 0.16 }));
        break;
      case 'record':
        [60, 64, 67, 72, 76].forEach((n, i) => this.#tone({ type: 'square', freq: midi(n), dur: 0.16, vol: 0.06, t: t + 0.4 + i * 0.09 }));
        this.#tone({ type: 'triangle', freq: midi(84), dur: 0.6, vol: 0.12, t: t + 0.85 });
        break;
    }
  }

  #tone({ type = 'sine', freq, freqEnd, dur = 0.15, vol = 0.2, t, attack = 0.005, dest = this.sfx }) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (freqEnd) osc.frequency.exponentialRampToValueAtTime(freqEnd, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(dest);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  #noise({ dur = 0.2, vol = 0.2, type = 'lowpass', freq = 1000, freqEnd, q = 0.8, t, dest = this.sfx }) {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(freq, t);
    if (freqEnd) filter.frequency.exponentialRampToValueAtTime(freqEnd, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(dest);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
  }

  /** Gruñido del monstruo: diente de sierra grave con vibrato, filtrado. */
  #growl(t, dur) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(75, t);
    osc.frequency.linearRampToValueAtTime(55, t + dur);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 23;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 18;
    lfo.connect(lfoGain).connect(osc.frequency);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 520;
    filter.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.4, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(filter).connect(g).connect(this.sfx);
    for (const o of [osc, lfo]) {
      o.start(t);
      o.stop(t + dur + 0.05);
    }
    this.#noise({ dur: dur * 0.8, vol: 0.12, type: 'lowpass', freq: 400, t });
  }

  // ---------- Música ----------

  /** Programa las semicorcheas que caen dentro de la ventana de anticipación. */
  #schedule() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    if (!this.mode || !this.enabled) {
      this.nextNote = ctx.currentTime + 0.05;
      return;
    }
    const run = this.mode === 'run';
    const bpm = run ? 100 + this.intensity * 32 : 78;
    const sixteenth = 60 / bpm / 4;
    // Si la pestaña se ha congelado, no intentamos recuperar las notas perdidas.
    if (this.nextNote < ctx.currentTime - 0.2) this.nextNote = ctx.currentTime + 0.05;
    while (this.nextNote < ctx.currentTime + LOOKAHEAD) {
      this.#playStep(this.step, this.nextNote, sixteenth, run);
      this.nextNote += sixteenth;
      this.step = (this.step + 1) % 64;
    }
  }

  #playStep(step, t, sixteenth, run) {
    const chord = CHORDS[Math.floor(step / 16)];
    const inBar = step % 16;
    const m = this.music;
    // Peligro: latido grave a contratiempo (suena aunque la música esté quitada).
    if (run && this.danger && (inBar === 3 || inBar === 11)) this.#tone({ type: 'sine', freq: 62, freqEnd: 40, dur: 0.14, vol: 0.5, t, dest: this.sfx });
    if (!this.musicOn) return;
    if (inBar === 0) {
      // Colchón: acorde largo y suave.
      for (const n of chord) this.#tone({ type: 'sawtooth', freq: midi(n), dur: sixteenth * 16, vol: 0.018, t, attack: 0.5, dest: m });
    }
    if (inBar === 0 || inBar === 8 || (run && (inBar === 6 || inBar === 14))) {
      this.#tone({ type: 'triangle', freq: midi(chord[0] - 12), dur: sixteenth * 3, vol: 0.28, t, dest: m });
    }
    if (inBar % 2 === 0) {
      const i = ARP[(inBar / 2) % ARP.length];
      const n = i === 3 ? chord[0] + 12 : chord[i];
      this.#tone({ type: 'sine', freq: midi(n + 12), dur: sixteenth * 1.8, vol: run ? 0.08 : 0.06, t, dest: m });
    }
    if (run) {
      if (inBar % 4 === 0) this.#tone({ type: 'sine', freq: 150, freqEnd: 45, dur: 0.16, vol: 0.45, t, dest: m });
      if (inBar % 4 === 2 && this.intensity > 0.15) this.#noise({ dur: 0.04, vol: 0.06, type: 'highpass', freq: 7000, t, dest: m });
    }
  }
}
